/** '' at cap.mabacmedia.com; '/cap' when built with VITE_BASE=/cap/ for partners proxy */
const BASE_URL = (import.meta.env.BASE_URL || '/').replace(/\/$/, '')

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

/** Longest error body we will quote back to the affiliate verbatim. */
const MAX_QUOTED_BODY = 200

/**
 * Turn a response body into something an affiliate (and whoever they forward it to)
 * can act on.
 *
 * The body is read ONCE, as text, then parsed. Reading it as JSON first and falling
 * back to `.text()` cannot work — `.json()` consumes the stream, so the fallback
 * always throws "body already read" and the message collapsed to the literal string
 * "Unknown error", which is what affiliates were reporting on failed cap requests.
 * The HTTP status is now always in the message, so even an unparseable body says
 * something.
 */
function describeFailure(status: number, statusText: string, body: string): string {
  const trimmed = body.trim()
  if (trimmed) {
    try {
      const parsed = JSON.parse(trimmed)
      if (parsed && typeof parsed === 'object') {
        const err = (parsed as { error?: unknown }).error
        if (typeof err === 'string' && err.trim()) return err
      }
      return `${status}: ${JSON.stringify(parsed)}`
    } catch {
      // Not JSON — an HTML error page, a proxy banner, or a bare status line.
      if (!trimmed.startsWith('<') && trimmed.length <= MAX_QUOTED_BODY) {
        return `${status}: ${trimmed}`
      }
    }
  }
  const label = statusText.trim() || 'request failed'
  return `${label} (HTTP ${status}). Please try again, or send this code to your account manager.`
}

export async function fetchAPI<T>(
  path: string,
  options: RequestInit = {},
  signal?: AbortSignal
): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    signal,
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  })

  // Read once, decide after — see describeFailure().
  const raw = await res.text().catch(() => '')

  if (!res.ok) {
    throw new ApiError(res.status, describeFailure(res.status, res.statusText, raw))
  }

  try {
    return JSON.parse(raw) as T
  } catch {
    // A 2xx that isn't JSON means we were served the SPA shell (or a proxy page)
    // where an API response was expected — say that, don't leak a parser error.
    throw new ApiError(res.status, `Unexpected non-JSON response from ${path} (HTTP ${res.status})`)
  }
}
